import { Modal, Form } from "react-bootstrap";
import { useEffect, useMemo } from "react";
import { Alphabet, At, Key, Send, Telephone } from "react-bootstrap-icons";
import { useForm } from "react-hook-form";
import { HeaderText } from "./Controls/HeaderText";
import { Input } from "./Controls/Input";
import { PhoneInput } from "./Controls/PhoneInput";
import { Button } from "./Controls/Button";
import { Select } from "./Controls/Select";
import { UNASSIGNED_GROUP_ID } from "./SubscriptionManageGroupParkersPage";
import { DateTimeFormatter, LocalDate } from "@js-joda/core";

export interface InviteParkerModalProps {
    show: boolean;
    setShow: (show: boolean) => void;
    submit: (data: InviteParkerForm) => void;
    plans?: any[];
    groups?: any[];
    isFromGroupContext?: boolean;
    isEnhancedGroup: boolean;
    isGroup?: boolean;
    /** When set, the group dropdown is pre-selected to this group when the modal opens (group context only). */
    defaultSubscriptionGroupId?: number | null;
    /** Subscription start date. When set, the start date picker minimum (and default) is this date, or today if it's in the past. */
    subscriptionStart?: LocalDate;
}

export interface InviteParkerForm {
    name: string;
    phoneNumber: string;
    emailAddress: string;
    isLoginEnabled: boolean;
    planId: number;
    subscriptionGroupId?: number | null;
    startDate: string;
}

export function InviteParkerModal({ show, setShow, submit, plans, groups, isEnhancedGroup, isGroup = false, isFromGroupContext = false, defaultSubscriptionGroupId, subscriptionStart }: InviteParkerModalProps) {
    const today = LocalDate.now();
    const minStartDate = (!subscriptionStart || subscriptionStart.isBefore(today) ? today : subscriptionStart)
        .format(DateTimeFormatter.ISO_LOCAL_DATE).toString();

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch }
        = useForm<InviteParkerForm>(
            { defaultValues: { name: "", emailAddress: "", phoneNumber: "", isLoginEnabled: false,
                 subscriptionGroupId: UNASSIGNED_GROUP_ID, startDate: minStartDate, } });

    const isLoginEnabledValue = watch("isLoginEnabled");
    const eligiblePlans = useMemo(
        () => (plans ?? []).filter((plan) => !plan.isUsageBasedBilling),
        [plans]
    );

    // When modal opens with a default group (group context), pre-select the group dropdown
    useEffect(() => {
        if (show && groups && defaultSubscriptionGroupId != null && defaultSubscriptionGroupId !== UNASSIGNED_GROUP_ID) {
            setValue("subscriptionGroupId", defaultSubscriptionGroupId);
        } else if (show && groups) {
            setValue("subscriptionGroupId", UNASSIGNED_GROUP_ID);
        }
    }, [show, defaultSubscriptionGroupId, groups, setValue]);
    function submitImpl(data: InviteParkerForm) {
        setShow(false);
        submit(data);
        reset();
    }

    return <Modal centered show={show} onEscapeKeyDown={() => setShow(false)}>
        <form onSubmit={handleSubmit(submitImpl)}>
            <Modal.Header>
                <HeaderText>Add Parker</HeaderText>
            </Modal.Header>
            <Modal.Body>
                {!isFromGroupContext && (
                <>
                <div className="text-secondary gap-4 px-2 mb-4" style={{ display: "grid", gridTemplateColumns: "auto 1fr", alignItems: "center" }}>
                    <Send size="32" className="text-primary" /> <span>Invite someone to your plan to let them park!</span>
                </div>
                <div className="text-secondary gap-4 px-2 mb-3" style={{ display: "grid", gridTemplateColumns: "auto 1fr", alignItems: "center" }}>
                    <Key size="32" className="text-primary" /> <span>You can add or remove spots or access from them at any time</span>
                </div>
                </>
                )}
               
                <Input
                    autoFocus
                    type="text"
                    label="Name"
                    icon={<Alphabet className="fs-5" />}
                    placeholder="Enter their Name"
                    {...register("name", { required: "Name is required" })}
                    error={errors.name}
                />

                <PhoneInput
                    icon={<Telephone className="fs-5" />}
                    label="Phone Number (Optional)"
                    placeholder="555-555-5555"
                    value={watch("phoneNumber") ?? ""}
                    {...register("phoneNumber", {
                        pattern: {
                            value: /^$|^\d{3}-\d{3}-\d{4}$/,
                            message: "Phone number must be in format ###-###-####",
                        },
                    })}
                    error={errors.phoneNumber}
                />
                
                {!isFromGroupContext &&
                <Form.Check
                    type="switch"
                    id="isLoginEnabled"
                    label="Invite parker to manage their account?"
                    {...register("isLoginEnabled")}
                    className="mb-3"
                />
                }

                {isLoginEnabledValue && (
                    <Input
                    type="email"
                    label="E-mail Address"
                    icon={<At className="fs-5" />}
                    placeholder="Enter their Email address"
                    {...register("emailAddress", { required: "Email Address is required" })}
                    error={errors.emailAddress}
                />
                )}
                {eligiblePlans.length > 0 && !isFromGroupContext && (
                        <Select label="Plan"
                            {...register("planId", { valueAsNumber: true, required: "Plan is required" })}
                            >
                            {eligiblePlans.map( plan => (
                                <option key={plan.planId} value={plan.planId}>
                                    {plan.name}
                                </option>
                            ))}
                        </Select>
                )}
                {groups && isFromGroupContext && (
                        <Select label="Group"
                            {...register("subscriptionGroupId", { valueAsNumber: true})}
                            >
                            <option key={UNASSIGNED_GROUP_ID} value={UNASSIGNED_GROUP_ID}>
                                    Unassigned
                            </option>
                            {groups.map( group => (
                                <option key={group.id} value={group.id}>
                                    {group.name}
                                </option>
                            ))}
                        </Select>
                )}

                {(isEnhancedGroup || isGroup) && (
                    <Form.Group className="mb-3">
                    <Form.Label>Start date</Form.Label>
                    <Form.Control
                        type="date"
                        min={minStartDate}
                        {...register("startDate", {
                        required: "Start date is required",
                        })}
                    />
                    {errors.startDate && (
                        <div className="text-danger small">
                        {errors.startDate.message}
                        </div>
                    )}
                    </Form.Group>
                )}
            {isFromGroupContext && (
            <div className="text-secondary gap-4 px-2 mb-3" style={{ display: "grid", gridTemplateColumns: "auto 1fr", alignItems: "center" }}>
                <span>You can add vehicles from the grid after saving</span>
            </div>
            )}
             
            </Modal.Body>
            <Modal.Footer className="d-flex">
                <Button className="flex-grow-1" onClick={() => {setShow(false); reset(); }}>
                    Cancel
                </Button>
                <Button className="flex-grow-1 btn-main" type="submit">
                    Confirm
                </Button>
            </Modal.Footer>
        </form>
    </Modal>
}